import React, { Ref, ReactElement, useCallback, ReactNode } from 'react';

import PropTypes, { Validator } from 'prop-types';
import { TableInstance } from 'react-table';
import {
  WindowScroller,
  List,
  ListProps,
  ListRowRenderer,
} from 'react-virtualized';
import styled from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';

import { TABLE_SIZES, TABLE_SIZE_THEMES } from './consts';
import TableBodyRow, { TableBodyRowProps } from './TableBodyRow';
import TableHeaderRow, { TableHeaderRowProps } from './TableHeaderRow';
import TablePaginationFooter from './TablePaginationFooter';
import TableSizeThemeContext from './TableSizeThemeContext';
import useTable, { UseTable } from './useTable';

type RenderProps<D extends Record<string, unknown>> = {
  table: TableInstance<D>;
};
type WithoutVirtualList = {
  hasVirtualList?: boolean;
  virtualListScrollElement?: never;
  virtualListProps?: never;
};
type WithVirtualList = {
  hasVirtualList: true;
  virtualListScrollElement?: typeof window | HTMLElement;
  virtualListProps?: Partial<ListProps>;
};
type VirtualList = WithoutVirtualList | WithVirtualList;
export type TableProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> &
    UseTable<D> &
    VirtualList & {
      renderHeaderRow?: (props: TableHeaderRowProps<D>) => ReactNode;
      renderBodyRow?: (props: TableBodyRowProps<D>) => ReactNode;
      renderLastRow?: (props: RenderProps<D>) => ReactNode;
      renderEmptyState?: (props: RenderProps<D>) => ReactNode;
      renderFooter?: (props: RenderProps<D>) => ReactNode;
      hasHiddenHeader?: boolean;
      hasStickyHeader?: boolean;
    };
export type TableRef<D extends Record<string, unknown>> = TableInstance<D> & {
  scrollToRow: (
    rowIndex: number,
    scrollOptions?: ScrollIntoViewOptions
  ) => void;
};

const virtualListClassOverride = 'virtualListClassOverride';
const VirtualListWrapper = styled.div`
  .${virtualListClassOverride} {
    width: 100% !important;
    overflow: unset !important;
    > [role='rowgroup'] {
      max-width: unset !important;
      width: 100% !important;
      overflow: unset !important;
    }
  }
`;

/** A component for rendering tabular data */
const TableWrapped = <
  D extends Record<string, unknown> = Record<string, unknown>
>(
  {
    columns,
    data,
    defaultSize = TABLE_SIZES.MEDIUM,
    renderHeaderRow = (props) => <TableHeaderRow<D> {...props} />,
    renderBodyRow = (props) => <TableBodyRow<D> {...props} />,
    renderLastRow,
    renderEmptyState,
    renderFooter: renderFooterProp,
    selectedRowIds,
    disabledRowIds,
    onAction,
    isSortable,
    isResizable,
    isColumnReorderable,
    isSingleSelectable,
    isMultiSelectable,
    isExpandable,
    hasHiddenHeader,
    hasStickyHeader,
    isPaginatable,
    manualPagination,
    pageSize,
    pageIndex,
    totalDataCount,
    hasVirtualList,
    virtualListScrollElement,
    virtualListProps = {},
    setTableOptions,
    setPluginHooks,
    storageKey,
    style,
    ...props
  }: TableProps<D>,
  ref: Ref<TableRef<D>>
): ReactElement => {
  const table = useTable<D>({
    columns,
    data,
    defaultSize,
    selectedRowIds,
    disabledRowIds,
    onAction,
    isSortable,
    isResizable,
    isColumnReorderable,
    isSingleSelectable,
    isMultiSelectable,
    isExpandable,
    isPaginatable,
    manualPagination,
    pageSize,
    pageIndex,
    totalDataCount,
    storageKey,
    setTableOptions,
    setPluginHooks,
  });
  const renderableRows = isPaginatable ? table.page : table.rows;

  const renderFooter = useCallback(
    ({ table }) => {
      if (renderFooterProp) return renderFooterProp({ table });
      if (isPaginatable) {
        return <TablePaginationFooter {...table} />;
      }
      return null;
    },
    [renderFooterProp, isPaginatable]
  );

  const tableRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(ref, () => ({
    ...table,
    scrollToRow: (rowIndex, scrollOptions = {}) => {
      if (hasVirtualList) {
        console.error(
          "scrollToRow is currently not supported for Table's that use virtual lists"
        );
      } else {
        if (tableRef.current) {
          const row = tableRef.current.querySelector(
            `[role='row'][data-index='${rowIndex}']`
          );
          row?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            ...scrollOptions,
          });
        }
      }
    },
  }));

  const rowRenderer = useCallback(
    ({ row, rowProps = {} }) => {
      table.prepareRow(row);
      return renderBodyRow({ row, ...row.getRowProps(rowProps) });
    },
    [renderBodyRow, table]
  );

  const virtualRowRenderer: ListRowRenderer = useCallback(
    (virtualRowProps) => {
      const row = renderableRows[virtualRowProps.index];
      return rowRenderer({ row, rowProps: { style: virtualRowProps.style } });
    },
    [renderableRows, rowRenderer]
  );

  const [scrollElement, setScrollElement] = React.useState<HTMLDivElement>();

  const tableSizeTheme = table.tableSizeTheme || TABLE_SIZE_THEMES[defaultSize];

  const { style: tableStyle, ...tableProps } = table.getTableProps();

  return (
    <TableSizeThemeContext.Provider
      value={{
        tableSizeTheme,
        setTableSizeTheme: table.setTableSizeTheme,
        resetTableSizeTheme: table.resetTableSizeTheme,
      }}
    >
      <div
        {...tableProps}
        {...props}
        ref={tableRef}
        css={[
          {
            ...tableStyle,
            display: 'flex',
            flexFlow: 'column nowrap',
            width: '100%',
            height: '100%',
            '[data-sticky-last-left-td]': {
              boxShadow: `2px 0 2px ${theme.colors.charcoal100}`,
              borderRight: 0,
            },
            '[data-sticky-first-right-td]': {
              boxShadow: `-2px 0 2px ${theme.colors.charcoal100}`,
              borderRight: 0,
            },
          },
          style,
        ]}
      >
        <div
          css={[
            {
              overflow: 'auto',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexFlow: 'column nowrap',
            },
          ]}
          ref={(el) => setScrollElement(el || undefined)}
        >
          <WindowScroller
            scrollElement={virtualListScrollElement || scrollElement}
          >
            {({ height, isScrolling, registerChild, scrollTop }) => (
              <>
                {!hasHiddenHeader && (
                  <div
                    style={
                      hasStickyHeader
                        ? {
                            position: 'sticky',
                            width: '100%',
                            minWidth: 'fit-content',
                            top: 0,
                            zIndex: 4,
                            boxShadow: `0 2px 2px ${theme.colors.charcoal100}`,
                          }
                        : {}
                    }
                  >
                    {table.headerGroups.map((headerGroup, index) => {
                      const { style, ...headerGroupProps } =
                        headerGroup.getHeaderGroupProps();
                      return renderHeaderRow({
                        ...headerGroupProps,
                        style: { ...style },
                        headerGroup,
                        isPrimaryRow: table.headerGroups.length <= index + 1,
                        isResizingAnyColumn:
                          !!table.state.columnResizing?.isResizingColumn,
                      });
                    })}
                  </div>
                )}
                {!data || !data.length ? (
                  <div
                    ref={registerChild}
                    css={[
                      {
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        height: '100%',
                      },
                    ]}
                  >
                    {renderEmptyState?.({ table })}
                  </div>
                ) : hasVirtualList ? (
                  <VirtualListWrapper
                    ref={registerChild}
                    {...table.getTableBodyProps()}
                  >
                    <List
                      autoHeight
                      className={virtualListClassOverride}
                      height={height || 100 /* fallback for test rendering */}
                      width={table.totalColumnsWidth}
                      rowCount={
                        isPaginatable ? table.page.length : table.rows.length
                      }
                      rowHeight={tableSizeTheme.rowHeight}
                      scrollTop={scrollTop}
                      isScrolling={isScrolling}
                      rowRenderer={virtualRowRenderer}
                      {...virtualListProps}
                    />
                    {renderLastRow && renderLastRow({ table })}
                  </VirtualListWrapper>
                ) : (
                  <div ref={registerChild}>
                    {renderableRows.map((row, index) =>
                      rowRenderer({ row, index })
                    )}
                    {renderLastRow && renderLastRow({ table })}
                  </div>
                )}
              </>
            )}
          </WindowScroller>
        </div>
        {renderFooter({ table })}
      </div>
    </TableSizeThemeContext.Provider>
  );
};

const Table = React.forwardRef(TableWrapped);

Table.displayName = 'Table';
Table.propTypes = {
  /* A list of objects representing the configuration for each column of the table.
   * Each of the column properties can be found here:
   * https://react-table.tanstack.com/docs/api/useTable#column-options
   */
  columns: PropTypes.arrayOf(PropTypes.any).isRequired,
  /* An array of rows that will fill in the table
   * The object keys should match up with the column's 'accessor' property.
   */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      /* subRow field holds rows which are nested under this row, typically used with
       * isExpandable set to true
       */
      subRow: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired
  ),
  /* An object which represents the row's that are currently selected on the table. The
   * keys are the row id's and the values are typically true. The system doesn't care
   * about the values, only the inclusion of the keys within the object. If a key of a
   * valid row id exists in the object, then the row is considered selected.
   */
  selectedRowIds: PropTypes.shape({}),
  /* An object which represents the row's that are currently disabled on the table. The
   * keys are the row id's and the values are typically true. The system doesn't care
   * about the values, only the inclusion of the keys within the object. If a key of a
   * valid row id exists in the object, then the row is considered disabled.
   */
  disabledRowIds: PropTypes.shape({}),
  /* Defines the default size used when the table first renders. After first render, the
   * size prop is controlled by the Table
   */
  defaultSize: PropTypes.oneOf(Object.values(TABLE_SIZES)),
  /* A render function which is called for each table header row. It defaults to the
   * TableHeaderRow component.
   */
  renderHeaderRow: PropTypes.func,
  /* A render function which is called for each table row. It is called with references to the
   * table instance, the current row, and the table theme. In most cases this function
   * should return <TableBodyRow> with a flat list of <TableBodyCell> as children, but it
   * can render any react node.
   */
  renderBodyRow: PropTypes.func,
  /* A render function which is called once, after the last data row. It is called with
   * references to the table instance. In most cases this function should return
   * <TableBodyRow> with a child of <LoadMoreDataRow /> although it can render any react
   * node.
   */
  renderLastRow: PropTypes.func,
  /* A render function which is called when 'data' is undefined or an empty array. It must
   * return a valid react node. Typically used to show a 'loading' or a 'no results' ui
   * beneath the column headers.
   */
  renderEmptyState: PropTypes.func,
  /* A render function which is useful for rendering content at the bottom of the table.
   * It can be used with isPaginatable to supply a custom pagination handler.
   */
  renderFooter: PropTypes.func,
  /* An event listener, which is called whenever the table state changes.
   * It will receive the current state, previous state, and the action meta data.
   */
  onAction: PropTypes.func,
  /* Enables sorting for all columns, however sorting can be disable on individual columns
   * via the 'disableSortBy' boolean property.
   */
  isSortable: PropTypes.bool,
  /* Enables resizing for all columns, however resizing can be disabled on individual
   * columns via the 'disableResizing' boolean property.
   */
  isResizable: PropTypes.bool,
  /* Enables reordering of columns, sticky columns cannot be reordered.  Reordering is
   * done in the configuration menu.
   */
  isColumnReorderable: PropTypes.bool,
  /* Enables all rows to be selectable. If you need to disable selectability for a
   * specific row, you will need to eject to using renderBodyRow, which will give you
   * finer control over how you determine if a row is selectable.
   * This flag by default adds a fixed, left side column to render the select checkboxes.
   */
  isMultiSelectable: PropTypes.bool as Validator<false>,
  // Enables only one row to be selectable at a time.
  isSingleSelectable: PropTypes.bool as Validator<true>,
  /* Enables all rows which have a 'subRows' array (aka. are expandable) to be expanded.
   * This flag by default adds a fixed, left side column to render the expansion carot
   * icon buttons.
   */
  isExpandable: PropTypes.bool,
  /* Hides all of the column headers, leaving only the data rows visible if they exist */
  hasHiddenHeader: PropTypes.bool,
  /* Forces the entire header group to stay at the top of the table's div when scrolling */
  hasStickyHeader: PropTypes.bool,
  /* Determines if the Table ought to have a pagination component hooked up to the footer.
   * This will configure react-table's usePagination hook as well as render the UI for
   * pagination.
   */
  // type casting to hack around propType union for pagination
  isPaginatable: PropTypes.bool as Validator<true>,
  /* Determines if pagination control is handled outside of the Table or inside of the
   * Table. Set to true if server side pagination is desired. Set to false if you already
   * have the entire `data` object in memory and want to use pagination simply for limiting
   * table size visually.
   * This will have no effect if `isPaginatable` is false or undefined.
   */
  // type casting to hack around propType union for pagination
  manualPagination: PropTypes.bool as Validator<true>,
  /* When using the pagination footer, the page size determines how many rows are
   * rendered on each page. Defaults to 50
   */
  pageSize: PropTypes.number,
  /* When using the pagination footer in manual mode, the page index determines which of
   * the set of pages is currently rendered. Defaults to 0
   */
  // type casting to hack around propType union for pagination
  pageIndex: PropTypes.number as Validator<number>,
  /* When using the pagination footer in manual mode, the page count determines the total
   * number of pages for the table.
   */
  // type casting to hack around propType union for pagination
  totalDataCount: PropTypes.number as Validator<number>,
  /* A function which has the default tableOptions as its only argument and allows the
   * consumer to forward the defaults or customize the entire object. The return value
   * should be a valid tableOption object
   */
  setTableOptions: PropTypes.func,
  /* A function which gets passed the default pluginHooks from react-table before
   * initialization. You can filter out or add in any pluginHooks that you desire. The
   * return value should be an array of valid pluginHooks.
   */
  setPluginHooks: PropTypes.func,
  /* A boolean to determine if the table should optimize rendering by only mounting rows
   * that are currently in the viewport or are about to be in the viewport.
   */
  hasVirtualList: PropTypes.bool as Validator<true>,
  /* A reference to either the global window object for full page scrolling or to the html
   * element which contains the table and is handling the overflow css property.
   */
  virtualListScrollElement: PropTypes.oneOfType([
    PropTypes.object, // global window object,
    PropTypes.element,
  ]) as Validator<HTMLElement>,
  /* An object which relays props to the react-virtualized List component. A full list of
   * props can be found here: https://github.com/bvaughn/react-virtualized/blob/master/docs/List.md
   */
  virtualListProps: PropTypes.shape({}),
  /**
   * unique key for local persistence of column state(width, hidden, sticky, etc) if set
   */
  storageKey: PropTypes.string,
  /* Styles applied to the root element */
  style: stylePropType,
  /* Class applied to the root element */
  className: PropTypes.string,
};

export default Object.assign(Table, { TABLE_SIZES }) as <
  D extends Record<string, unknown>
>(
  p: TableProps<D> & { ref?: Ref<TableRef<D>> }
) => ReactElement;
